import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import PaymentApiService from "../../ApiServices/PaymentApiServices/PaymentApiService";
import Topbar from "../Side/Topbar";
import AdminSidebar from "../Side/AdminSidebar";
import Sidebar from "../Side/Sidebar";
import { useAuth } from "../Auth/AuthContext";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Search } from "lucide-react";

const AdminPaymentInfo = () => {
  const { authState } = useAuth();
  const admin = authState.roles === "Admin";

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [methodFilter, setMethodFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const fetchPaymentData = async () => {
    try {
      const response = await PaymentApiService.Get();
      setData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentData();
  }, []);

  useEffect(() => {
    let filtered = data;

    // Filter by name
    if (searchText) {
      filtered = filtered.filter((item) =>
        (item.user.firstName + " " + item.user.lastName)
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    }

    // Filter by method
    if (methodFilter !== "All") {
      filtered = filtered.filter((item) => item.method === methodFilter);
    }

    // Filter by status
    if (statusFilter !== "All") {
      filtered = filtered.filter((item) =>
        statusFilter === "Active" ? item.isActive : !item.isActive
      );
    }

    setFilteredData(filtered);
  }, [searchText, methodFilter, statusFilter, data]);

  const customStyles = {
    rows: {
      style: {
        minHeight: "60px",
        borderBottom: "1px solid #E5E7EB",
        transition: "all 0.2s ease-in-out",
        '&:hover': {
          backgroundColor: "#F3F4F6",
          transform: "scale(1.01)",
        },
      },
    },
    headCells: {
      style: {
        backgroundColor: "#F9FAFB",
        color: "#1F2937",
        fontWeight: "700",
        fontSize: "14px",
        padding: "16px",
      },
    },
    cells: {
      style: {
        padding: "16px",
        fontSize: "14px",
        color: "#374151",
      },
    },
  };

  const columns = [
    {
      name: "Publisher",
      selector: (row) => row.user.firstName + " " + row.user.lastName,
      sortable: true,
    },
    {
      name: "Method",
      selector: (row) => row.method,
      sortable: true,
    },
    {
      name: "Details",
      cell: (row) => (
        <div className="text-sm space-y-1 text-gray-700 leading-5">
          {row.method === "BTC" && <p><strong>BTC:</strong> {row.btcAddress}</p>}
          {row.method === "PayPal" && <p><strong>PayPal:</strong> {row.paypalEmail}</p>}
          {row.method === "Payoneer" && <p><strong>Payoneer:</strong> {row.payoneerEmail}</p>}
          {row.method === "Bank" && (
            <>
              <p><strong>Bank:</strong> {row.bankName}</p>
              <p><strong>Acc Name:</strong> {row.accountName}</p>
              <p><strong>Acc No:</strong> {row.accountNumber}</p>
              <p><strong>SWIFT:</strong> {row.swiftCode}</p>
            </>
          )}
        </div>
      ),
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${row.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
          {row.isActive ? <CheckCircle size={14} /> : <XCircle size={14} />}
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
      sortable: true,
    },
  ];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-100">
      {admin ? <AdminSidebar /> : <Sidebar />}
      <div className="flex-1 flex flex-col">
        <Topbar />
        <main className="p-6 pt-24">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto bg-white/80 border border-gray-200 backdrop-blur-lg rounded-3xl shadow-2xl p-8"
          >
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between mb-6">
              <div>
                <h2 className="text-2xl font-extrabold text-gray-800">Publishers Payment Info</h2>
                <p className="text-sm text-gray-500">Search & filter by payment method or status</p>
              </div>
              <span className="text-sm text-gray-500">📅 {new Date().toLocaleDateString()}</span>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by publisher name"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>
              <select
                value={methodFilter}
                onChange={(e) => setMethodFilter(e.target.value)}
                className="w-full py-2 px-4 border rounded-md text-sm border-gray-300 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              >
                <option value="All">All Methods</option>
                <option value="PayPal">PayPal</option>
                <option value="Payoneer">Payoneer</option>
                <option value="BTC">BTC</option>
                <option value="Bank">Bank</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full py-2 px-4 border rounded-md text-sm border-gray-300 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              >
                <option value="All">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Table */}
            <DataTable
              columns={columns}
              data={filteredData}
              progressPending={loading}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
              noHeader
            />
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AdminPaymentInfo;
